/**
 * A che punto e' ogni partecipante: non ha ancora scelto, ha inviato la
 * richiesta, ha un'assegnazione in bozza, e' confermato (ha ricevuto l'email).
 */

import type { PartecipanteEvento } from './tipi';

export type StatoPartecipante = 'da_scegliere' | 'richiesta' | 'bozza' | 'confermata';

export const ORDINE_STATI: StatoPartecipante[] = ['da_scegliere', 'richiesta', 'bozza', 'confermata'];

/** badge = la pastiglia; colore = il pallino e il pezzo di barra dell'avanzamento. */
export const STATI: Record<StatoPartecipante, { etichetta: string; plurale: string; badge: string; colore: string; spiegazione: string }> = {
  da_scegliere: {
    etichetta: 'Non ha ancora scelto',
    plurale: 'Non hanno ancora scelto',
    badge: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    colore: 'bg-neutral-300',
    spiegazione: "Ha ricevuto (o ricevera') il link personale ma non ha ancora inviato la sua scelta.",
  },
  richiesta: {
    etichetta: 'Richiesta inviata',
    plurale: 'Richieste da assegnare',
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    colore: 'bg-amber-400',
    spiegazione: "Ha scelto il suo percorso: aspetta che SLAB gli assegni i posti.",
  },
  bozza: {
    etichetta: 'Assegnato (bozza)',
    plurale: 'Assegnati in bozza',
    badge: 'bg-violet-50 text-violet-800 border-violet-200',
    colore: 'bg-violet-400',
    spiegazione: "L'assegnazione e' pronta ma non e' ancora stata comunicata.",
  },
  confermata: {
    etichetta: 'Confermato',
    plurale: 'Confermati',
    badge: 'bg-green-50 text-green-800 border-green-200',
    colore: 'bg-green-500',
    spiegazione: "Ha ricevuto (o sta per ricevere) l'email con il percorso assegnato.",
  },
};

type PerStato = Pick<PartecipanteEvento, 'richiesta_at' | 'assegnazione_stato'>;

export function statoPartecipante(p: PerStato): StatoPartecipante {
  if (p.assegnazione_stato === 'confermata') return 'confermata';
  if (p.assegnazione_stato === 'bozza') return 'bozza';
  return p.richiesta_at ? 'richiesta' : 'da_scegliere';
}

const FUSO = 'Europe/Rome';

/** "3 ott, 10:15" in ora italiana (il database parla in ora di Londra). */
export function dataOraIt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('it-IT', {
    timeZone: FUSO,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "3 ott 2026" in ora italiana. */
export function dataIt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleDateString('it-IT', { timeZone: FUSO, day: 'numeric', month: 'short', year: 'numeric' });
}

/** Lo stato in una frase, con la data quando serve. */
export function descriviStato(p: PerStato & Pick<PartecipanteEvento, 'confermata_at'>): string {
  switch (statoPartecipante(p)) {
    case 'confermata':
      return `Confermato il ${dataOraIt(p.confermata_at)}`;
    case 'bozza':
      return 'Assegnato (bozza)';
    case 'richiesta':
      return `Richiesta inviata il ${dataOraIt(p.richiesta_at)}`;
    default:
      return 'Non ha ancora scelto';
  }
}

/**
 * Vero se la persona aveva gia' ricevuto il percorso e poi l'assegnazione e'
 * stata cambiata: riconfermando le arriva una nuova email.
 */
export function cambiataDopoLaConferma(p: PerStato & Pick<PartecipanteEvento, 'confermata_at'>): boolean {
  return p.assegnazione_stato === 'bozza' && Boolean(p.confermata_at);
}

export type ConteggioStati = Record<StatoPartecipante, number> & { totale: number };

export function contaStati(partecipanti: PerStato[]): ConteggioStati {
  const conti: ConteggioStati = { totale: partecipanti.length, da_scegliere: 0, richiesta: 0, bozza: 0, confermata: 0 };
  for (const p of partecipanti) conti[statoPartecipante(p)] += 1;
  return conti;
}

/** Filtra per stato e per testo (nome, email, azienda). */
export function filtraPartecipanti<T extends PerStato & Pick<PartecipanteEvento, 'nome' | 'email' | 'azienda'>>(
  partecipanti: T[],
  filtro: { stato: StatoPartecipante | 'tutti'; testo: string },
): T[] {
  const cerca = filtro.testo.trim().toLowerCase();
  return partecipanti.filter((p) => {
    if (filtro.stato !== 'tutti' && statoPartecipante(p) !== filtro.stato) return false;
    if (!cerca) return true;
    return [p.nome, p.email, p.azienda ?? ''].some((v) => v.toLowerCase().includes(cerca));
  });
}
